import { Button, Flex, message, Modal, theme, Typography } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import {
  shiftSelector,
  selectedOutletSelector,
  selectedPosOutletOptionsSelector,
  userSelector,
  posZeroLoadingSelector,
} from 'app-redux/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { setCloseShiftModalSuccess, setShift } from 'app-redux/cashierSlice'
import { IdcardOutlined, QuestionCircleOutlined, CloseOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import dayjs from 'dayjs'
import { useCheckShiftDevices, useCloseShift } from '@/utils/queries/shift'
import { POS_ONLINE_HEADER } from '@/utils/static'
import { isOptionEnabled } from '@/utils/helper'
import { getOrCreateDeviceId } from '@/utils/cookies'
import { normalizeShiftDevices } from '@/utils/shiftDevices'
import { countByStatus } from '@/utils/posLocalDb/repositories/asyncOps'
import { flushQueue } from '@/utils/posLocalDb/asyncUploader'
import { getQueueOwner } from '@/utils/posLocalDb/queueContext'
import useNavigation from '@/utils/hooks/useNavigation'
import { SYNC_HISTORY_PATH } from '../ZeroLoading/ZeroLoadingStatusBadge'
import CloseShiftForm from './CloseShiftForm'
import OpenDevicesBlockModal from './OpenDevicesBlockModal'
import PendingSyncBlockModal from './PendingSyncBlockModal'
import DateTimeDisplay from '../OpenShift/DateTimeDisplay'

const { Title, Text } = Typography

/**
 * `shift` (opsional): shift open milik kasir dari Riwayat Shift. Kosong = shift aktif kasir.
 */
const CloseShift = ({ onCancel, user: userProp, shift: shiftProp, ...props }) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const queryClient = useQueryClient()

  const outlet = useSelector(selectedOutletSelector)
  const activeShift = useSelector(shiftSelector)
  const shift = shiftProp ?? activeShift
  const outletOptions = useSelector(selectedPosOutletOptionsSelector)
  const loggedInUser = useSelector(userSelector) || userProp
  // Header menampilkan pemilik shift (bisa kasir lain dari riwayat), bukan kasir yang login.
  const user = shiftProp?.user ?? loggedInUser
  const isRequestInputBalanceOnCloseShift = isOptionEnabled(
    outletOptions?.is_request_input_balance_on_close_shift,
  )

  const { mutate, isLoading } = useCloseShift()
  const checkShiftDevices = useCheckShiftDevices()
  const { onNavigate } = useNavigation()
  const zeroLoading = useSelector(posZeroLoadingSelector)
  const [blockedDevices, setBlockedDevices] = useState([])
  const [pendingSync, setPendingSync] = useState(null)
  const [isChecking, setIsChecking] = useState(false)
  // Nilai form saat tutup shift diblokir device lain — dipakai ulang oleh Tutup Paksa.
  const blockedValuesRef = useRef(null)

  const userPhoto = useMemo(() => {
    return (
      user?.profile_image ||
      'https://lh3.googleusercontent.com/aida-public/AB6AXuAV18N2Yp8JZrkHoCG9GnXTUuj5_3KcmjjWWXiy5kh8WDB9qs6vZ9XPQVZSic-sccUnp6gSlTBKx50IbfkuLwF7eikChvxBGMWlyLbVKhM2HsMqdOJlb30UNwb0Q4zo4-Jt6_nwdYlAtpK2oqtlc8yfCoRGqmvJ_Dpor6kgXo9eK36Qy-6MLBjCbN7Y-Ur2a5Xmk6AFGDgiV3CdgQ1Ot3pBbd7fw6f1S4qDo2QY4wqQhQcrgn51MFeWNdlYn4rimGikGOhPzE6HRbo'
    )
  }, [user?.profile_image])

  const [imageError, setImageError] = useState(false)
  const [currentDateTime, setCurrentDateTime] = useState(dayjs())

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(dayjs())
    }, 60000) // Update every minute
    return () => clearInterval(interval)
  }, [])

  const shiftDuration = useMemo(() => {
    if (!shift?.start_time) {
      return ''
    }

    return `${dayjs(shift.start_time).format('DD MMMM YYYY HH:mm')} - ${currentDateTime.format(
      'DD MMMM YYYY HH:mm',
    )}`
  }, [shift?.start_time, currentDateTime])

  const shiftDurationText = useMemo(() => {
    if (!shift?.start_time) {
      return ''
    }

    const start = dayjs(shift.start_time)
    const end = currentDateTime
    const diff = end.diff(start)
    const dur = dayjs.duration(diff)

    const days = dur.days()
    const hours = dur.hours()
    const minutes = dur.minutes()

    const parts = []
    if (days > 0) {
      parts.push(`${days} ${formatMessage({ id: days === 1 ? 'date.day' : 'date.days' })}`)
    }
    if (hours > 0) {
      parts.push(`${hours} ${formatMessage({ id: hours === 1 ? 'date.hour' : 'date.hours' })}`)
    }
    if (minutes > 0 || parts.length === 0) {
      parts.push(
        `${minutes} ${formatMessage({ id: minutes === 1 ? 'date.minute' : 'date.minutes' })}`,
      )
    }

    return parts.join(' ')
  }, [shift?.start_time, currentDateTime, formatMessage])

  /**
   * Cek sebelum tutup shift: (1) Mode Zero-Loading — semua transaksi lokal harus sudah
   * tersinkron agar total shift di server lengkap; (2) multi-device — device lain yang
   * masih membuka shift ini harus tutup dulu. Cek device fail-open (error → lanjut).
   */
  const canCloseShift = useCallback(async () => {
    if (zeroLoading) {
      // Gagal baca antrean lokal (mis. storage ditolak) → anggap tak ada yang menunggu.
      const counts = await countByStatus(getQueueOwner()).catch((error) => {
        Sentry.captureException(error)
        return { pending: 0, sent: 0, failed: 0, blocked: 0 }
      })
      if (counts.pending + counts.sent + counts.failed + (counts.blocked ?? 0) > 0) {
        flushQueue()
        setPendingSync(counts)
        return false
      }
    }
    if (shift?.id) {
      try {
        const res = await checkShiftDevices.mutateAsync({
          shiftId: shift.id,
          apiOptions: POS_ONLINE_HEADER,
        })
        const deviceId = getOrCreateDeviceId()
        const others = normalizeShiftDevices(res).filter((d) => d.device_id !== deviceId)
        if (others.length) {
          setBlockedDevices(others)
          return false
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    }
    return true
  }, [checkShiftDevices, shift?.id, zeroLoading])

  const performCloseShift = useCallback(
    (values) => {
      const payload = { id: shift?.id, pin: values.pin }
      if (isRequestInputBalanceOnCloseShift) {
        payload.closing_balance = values?.closing_balance
      }
      mutate(
        { ...payload, apiOptions: POS_ONLINE_HEADER },
        {
          onSuccess: (response) => {
            if (response.data.success) {
              onCancel()
              dispatch(
                setCloseShiftModalSuccess({ open: true, data: { ...response.data.data, outlet } }),
              )
              // Refresh riwayat/detail shift; key `current` (validasi shift aktif) sengaja dilewati.
              queryClient.invalidateQueries({
                predicate: (query) =>
                  query.queryKey[0] === 'shifts' && query.queryKey[1] !== 'current',
              })
              // Shift dari riwayat (bukan shift aktif) tidak boleh mereset sesi kasir.
              if (String(activeShift?.id ?? '') === String(shift?.id)) {
                setTimeout(() => {
                  dispatch(setShift({}))
                }, 1000)
              }
            } else {
              message.error(response.data.message)
            }
          },
          onError: (error) => {
            message.error(error.data?.message || 'Failed to add data to the server')
          },
        },
      )
    },
    [
      shift?.id,
      activeShift?.id,
      isRequestInputBalanceOnCloseShift,
      mutate,
      onCancel,
      dispatch,
      outlet,
      queryClient,
    ],
  )

  const handleSubmit = useCallback(
    async (values) => {
      setIsChecking(true)
      const allowed = await canCloseShift().finally(() => setIsChecking(false))
      if (!allowed) {
        blockedValuesRef.current = values
        return
      }
      performCloseShift(values)
    },
    [canCloseShift, performCloseShift],
  )

  // Jalan keluar deadlock: device lain tak bisa ditutup (mati/hilang/terpasang ulang).
  // Antrean Zero-Loading browser ini sudah lolos sebelum modal device muncul.
  const handleForceClose = useCallback(() => {
    setBlockedDevices([])
    if (blockedValuesRef.current) {
      performCloseShift(blockedValuesRef.current)
    }
  }, [performCloseShift])

  const handleCloseBlockedDevices = useCallback(() => setBlockedDevices([]), [])

  const handleClosePendingSync = useCallback(() => setPendingSync(null), [])

  const handleOpenSyncCenter = useCallback(() => {
    setPendingSync(null)
    onCancel?.()
    onNavigate(SYNC_HISTORY_PATH)
  }, [onCancel, onNavigate])

  const handleHelp = useCallback(() => {
    const baseUrl = window.location.origin
    window.open(`${baseUrl}/#/faqs`, '_blank')
  }, [])

  const getContainer = useCallback(() => {
    const contentElement = document.getElementById('cashier-content-area')
    return contentElement || document.body
  }, [])

  return (
    <Modal
      title={null}
      destroyOnHidden
      footer={null}
      closable={false}
      maskClosable={false}
      width={680}
      styles={{
        body: { padding: 0 },
      }}
      getContainer={getContainer}
      onCancel={onCancel}
      {...props}
    >
      <Flex style={{ minHeight: 380 }}>
        {/* Left Panel - User Info */}
        <div
          className="close-shift-left-panel"
          style={{
            flex: '0 0 42%',
            position: 'relative',
            minHeight: 380,
            overflow: 'hidden',
            borderRadius: '8px 0 0 8px',
            cursor: 'default',
          }}
        >
          {/* Background Image with Hover Effect */}
          <div
            className="close-shift-bg-image"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundImage: imageError
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : `url(${userPhoto})`,
              transition: 'transform 0.7s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          />
          {/* Hidden img for error handling */}
          <img
            src={userPhoto}
            alt={user?.name}
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              opacity: 0,
              pointerEvents: 'none',
            }}
            onError={() => {
              setImageError(true)
            }}
          />

          {/* Glass Overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6))',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              padding: 16,
              color: '#fff',
            }}
          >
            {/* Active Session Badge */}
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                background: 'rgba(59, 130, 246, 0.2)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderRadius: 9999,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                marginBottom: 10,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                width: 'fit-content',
              }}
            >
              {formatMessage({ id: 'active_session' })}
            </span>

            {/* User Name */}
            <h1
              style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                lineHeight: 1.2,
                marginBottom: 4,
                color: '#fff',
              }}
            >
              {user?.name || 'User'}
            </h1>

            {/* Shift Duration */}
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.85rem',
                fontWeight: 400,
                marginBottom: 8,
              }}
            >
              {shiftDuration}
            </p>

            {/* Quote */}
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.8rem',
                fontWeight: 300,
                fontStyle: 'italic',
                marginBottom: 0,
              }}
            >
              &quot;{formatMessage({ id: 'close_shift_message' })}&quot;
            </p>

            {/* Employee ID Section */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 14,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IdcardOutlined
                  style={{
                    color: '#fff',
                    fontSize: 14,
                  }}
                />
              </div>
              <div>
                <p
                  style={{
                    fontSize: 10,
                    color: 'rgba(255, 255, 255, 0.6)',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    margin: 0,
                    marginBottom: 2,
                  }}
                >
                  {formatMessage({ id: 'employee_id' })}
                </p>
                <p
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#fff',
                    margin: 0,
                  }}
                >
                  {`EMP #${user?.id}`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div
          style={{
            flex: 1,
            background: token.colorBgContainer,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '0 8px 8px 0',
          }}
        >
          {/* Header */}
          <Flex justify="space-between" align="flex-start" style={{ marginBottom: 20 }}>
            <div>
              <Title level={2} style={{ margin: 0, marginBottom: 4, fontSize: 22 }}>
                {formatMessage({ id: 'close_shift' })}
              </Title>
              {shift?.start_time && (
                <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
                  {formatMessage({ id: 'shift_duration' })}: {shiftDurationText}
                </Text>
              )}
              {!!shift?.id && (
                <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
                  {formatMessage({ id: 'pos.shift_id' })}: #{shift.id}
                </Text>
              )}
            </div>
            <DateTimeDisplay />
          </Flex>

          {/* Form */}
          <div style={{ flex: 1 }}>
            <CloseShiftForm
              isRequestInputBalanceOnCloseShift={isRequestInputBalanceOnCloseShift}
              onSubmit={handleSubmit}
              loading={isLoading || isChecking}
            />
          </div>

          {/* Footer Links */}
          <Flex
            justify="center"
            gap={12}
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <Button
              type="link"
              icon={<CloseOutlined />}
              style={{ color: token.colorTextTertiary }}
              onClick={onCancel}
            >
              {formatMessage({ id: 'button.cancel' })}
            </Button>
            <div style={{ width: 1, height: 16, background: token.colorBorderSecondary }} />
            <Button
              type="link"
              icon={<QuestionCircleOutlined />}
              style={{ color: token.colorTextTertiary }}
              onClick={handleHelp}
            >
              {formatMessage({ id: 'help' })}
            </Button>
          </Flex>
        </div>
      </Flex>
      <PendingSyncBlockModal
        open={!!pendingSync}
        counts={pendingSync}
        onOpenSyncCenter={handleOpenSyncCenter}
        onClose={handleClosePendingSync}
      />
      <OpenDevicesBlockModal
        open={blockedDevices.length > 0}
        devices={blockedDevices}
        currentDeviceId={getOrCreateDeviceId()}
        onClose={handleCloseBlockedDevices}
        onForceClose={handleForceClose}
      />
    </Modal>
  )
}

export default CloseShift
